/**
 * ============================================================
 * Wingman RC5 — Indoor Airport Intelligence
 * IndoorTopologyForensics.ts
 *
 * Geometry audit engine: builds routable topology graph from
 * corridor, door, and connector geometry. Uses meter-based
 * Haversine distances with strict snap tolerances.
 *
 * IAM-M16B-R1.5 — Corridor + Door + Connector Topology
 * ============================================================
 */

import {
  type TopologyNode,
  type TopologyEdge,
  type TopologyGraph,
  type TopologyAuditReport,
  type DistancePolicy,
  type ITopologyBuilder,
  TopologyEdgeType,
  PRODUCTION_DISTANCE_POLICY,
} from './IndoorTopologyContracts';

// ── Haversine Distance ───────────────────────────────────────

const EARTH_RADIUS_METERS = 6_371_000;

/**
 * Compute Haversine distance between two [lon, lat] points in meters.
 */
export function haversineMeters(
  a: readonly [number, number],
  b: readonly [number, number],
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);

  const sinHalfDLat = Math.sin(dLat / 2);
  const sinHalfDLon = Math.sin(dLon / 2);

  const h = sinHalfDLat * sinHalfDLat +
    Math.cos(lat1) * Math.cos(lat2) * sinHalfDLon * sinHalfDLon;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
}

// ── Graph Builder ────────────────────────────────────────────

export class IndoorTopologyForensics implements ITopologyBuilder {
  /**
   * Build a topology graph from raw geometry nodes.
   *
   * Algorithm:
   * 1. Index all nodes by level.
   * 2. For same-level nodes within sameLevelSnapMeters, create ADJACENCY edges.
   * 3. For doors, connect to nearest corridors within snap tolerance.
   * 4. For connectors with connectedFloorIds, create LEVEL_TRANSITION edges
   *    to nearest nodes on the connected floor within crossLevelSnapMeters.
   */
  buildGraph(
    corridors: readonly TopologyNode[],
    doors: readonly TopologyNode[],
    connectors: readonly TopologyNode[],
    policy: DistancePolicy = PRODUCTION_DISTANCE_POLICY,
  ): TopologyGraph {
    const allNodes = [...corridors, ...doors, ...connectors];
    const nodeMap = new Map<string, TopologyNode>();
    const edges: TopologyEdge[] = [];
    const adjacencyList = new Map<string, TopologyEdge[]>();

    // Index nodes
    for (const node of allNodes) {
      nodeMap.set(node.id, node);
      adjacencyList.set(node.id, []);
    }

    // Group nodes by level
    const nodesByLevel = new Map<number, TopologyNode[]>();
    for (const node of allNodes) {
      if (!nodesByLevel.has(node.level)) {
        nodesByLevel.set(node.level, []);
      }
      nodesByLevel.get(node.level)!.push(node);
    }

    // ── Same-level adjacency ──
    for (const [, levelNodes] of nodesByLevel) {
      for (let i = 0; i < levelNodes.length; i++) {
        for (let j = i + 1; j < levelNodes.length; j++) {
          const a = levelNodes[i];
          const b = levelNodes[j];
          const dist = haversineMeters(a.coordinates, b.coordinates);

          if (dist <= policy.sameLevelSnapMeters) {
            const edge = this.createEdge(a, b, TopologyEdgeType.ADJACENCY, dist, 0);
            edges.push(edge);
            adjacencyList.get(a.id)!.push(edge);

            // Reverse edge
            const reverseEdge = this.createEdge(b, a, TopologyEdgeType.ADJACENCY, dist, 0);
            edges.push(reverseEdge);
            adjacencyList.get(b.id)!.push(reverseEdge);
          }
        }
      }
    }

    // ── Door connections ──
    for (const door of doors) {
      const sameLevelNodes = (nodesByLevel.get(door.level) ?? []).filter(n => n.id !== door.id);
      for (const neighbor of sameLevelNodes) {
        const dist = haversineMeters(door.coordinates, neighbor.coordinates);
        if (dist <= policy.sameLevelSnapMeters) {
          const edge = this.createEdge(
            door, neighbor,
            TopologyEdgeType.DOOR_CONNECTION, dist, 0,
          );
          edges.push(edge);
          adjacencyList.get(door.id)!.push(edge);

          const reverseEdge = this.createEdge(
            neighbor, door,
            TopologyEdgeType.DOOR_CONNECTION, dist, 0,
          );
          edges.push(reverseEdge);
          adjacencyList.get(neighbor.id)!.push(reverseEdge);
        }
      }
    }

    // ── Level transitions via connectors ──
    // Connector-to-connector pairs that share connectedFloorIds use the
    // sameLevelSnapMeters tolerance (they represent the same physical
    // structure spanning multiple floors, e.g., an escalator).
    // Other cross-level connections use the stricter crossLevelSnapMeters.
    for (const connector of connectors) {
      const connectedFloors = connector.connectedFloorIds ?? [];
      for (const targetFloor of connectedFloors) {
        if (targetFloor === connector.level) continue; // Skip self-level

        const targetLevelNodes = nodesByLevel.get(targetFloor) ?? [];
        for (const targetNode of targetLevelNodes) {
          const dist = haversineMeters(connector.coordinates, targetNode.coordinates);

          // Connector-to-connector pairs (same physical structure) use the
          // dedicated crossFloorPairMeters threshold.
          // Other cross-level connections use crossLevelSnapMeters.
          const isConnectorPair =
            targetNode.type === 'connector' &&
            (targetNode.connectedFloorIds ?? []).includes(connector.level);
          const snapTolerance = isConnectorPair
            ? policy.crossFloorPairMeters
            : policy.crossLevelSnapMeters;

          if (dist <= snapTolerance) {
            const penalty = policy.levelTransitionPenaltyMeters;
            const edge = this.createEdge(
              connector, targetNode,
              TopologyEdgeType.LEVEL_TRANSITION, dist, penalty,
            );
            edges.push(edge);
            adjacencyList.get(connector.id)!.push(edge);

            const reverseEdge = this.createEdge(
              targetNode, connector,
              TopologyEdgeType.LEVEL_TRANSITION, dist, penalty,
            );
            edges.push(reverseEdge);
            adjacencyList.get(targetNode.id)!.push(reverseEdge);
          }
        }
      }
    }

    return {
      nodes: nodeMap,
      edges,
      adjacencyList,
      nodeCount: nodeMap.size,
      edgeCount: edges.length,
    };
  }

  /**
   * Produce a forensic audit report for a topology graph.
   * Includes connected component analysis and reachability matrix.
   */
  audit(graph: TopologyGraph): TopologyAuditReport {
    const levels = new Set<number>();
    let corridorCount = 0;
    let doorCount = 0;
    let connectorCount = 0;

    for (const [, node] of graph.nodes) {
      levels.add(node.level);
      switch (node.type) {
        case 'corridor': corridorCount++; break;
        case 'door': doorCount++; break;
        case 'connector': connectorCount++; break;
      }
    }

    // ── Connected components via BFS ──
    const visited = new Set<string>();
    const reachabilityMatrix = new Map<string, Set<string>>();
    let connectedComponents = 0;

    for (const [nodeId] of graph.nodes) {
      if (visited.has(nodeId)) continue;
      connectedComponents++;

      const component = new Set<string>();
      const queue = [nodeId];
      visited.add(nodeId);

      while (queue.length > 0) {
        const current = queue.shift()!;
        component.add(current);

        const neighbors = graph.adjacencyList.get(current) ?? [];
        for (const edge of neighbors) {
          if (!visited.has(edge.to)) {
            visited.add(edge.to);
            queue.push(edge.to);
          }
        }
      }

      // Mark reachability for all nodes in this component
      for (const id of component) {
        reachabilityMatrix.set(id, component);
      }
    }

    return {
      airport: 'JFK',
      terminal: 'T4',
      timestamp: Date.now(),
      corridorCount,
      doorCount,
      connectorCount,
      nodeCount: graph.nodeCount,
      edgeCount: graph.edgeCount,
      levels: Array.from(levels).sort((a, b) => a - b),
      connectedComponents,
      isFullyConnected: connectedComponents === 1,
      reachabilityMatrix: reachabilityMatrix as ReadonlyMap<string, ReadonlySet<string>>,
    };
  }

  // ── Private Helpers ──────────────────────────────────────

  private createEdge(
    from: TopologyNode,
    to: TopologyNode,
    type: TopologyEdgeType,
    distanceMeters: number,
    penalty: number,
  ): TopologyEdge {
    // Minimum distance of 0.5m prevents zero-weight edges for co-located nodes
    const effectiveDistance = Math.max(distanceMeters, 0.5);
    return {
      from: from.id,
      to: to.id,
      type,
      distanceMeters: Math.round(effectiveDistance * 100) / 100,
      penalty,
      weight: Math.round((effectiveDistance + penalty) * 100) / 100,
    };
  }
}
